import { API_BASE_URL } from "../config/api";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";
import FloatingCalculator from "./FloatingCalculator";



export default function Layout() {
  const adminName = localStorage.getItem("adminName") || "Admin";
  const navigate  = useNavigate();
  const location  = useLocation();

  // State variables
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [profileOpen, setProfileOpen]   = useState(false);
  const [notifOpen, setNotifOpen]       = useState(false);
  const [notifications, setNotifications] = useState([]);
  const desktopNotifRef = useRef(null);
  const mobileNotifRef  = useRef(null);
  const desktopProfileRef = useRef(null);
  const mobileProfileRef  = useRef(null);
  const sidebarRef = useRef(null);

  const [expandedSection, setExpandedSection] = useState(null);

  /* Auto-expand active accordion section on desktop based on current route */
  useEffect(() => {
    const p = location.pathname;
    if (["/create-complaint", "/complaints", "/admin/travel", "/admin/daily-updates", "/admin/reports-list"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("operations");
    } else if (["/admin/quotations", "/admin/performa-invoices", "/admin/sales-invoices", "/admin/purchase-orders", "/admin/purchase-invoices", "/admin/delivery-challans", "/admin/project-expenses"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("sales-purchase");
    } else if (["/admin/vouchers", "/admin/gst-export-reports", "/admin/debit-credit-notes", "/admin/test-certificate-settings", "/admin/material-stock", "/admin/product-mapping"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("accounts");
    } else if (["/customers", "/customer-groups", "/admin/dealers", "/engineers", "/admin/accounts", "/admin/website-enquiries"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("contacts");
    } else if (["/admin/company-profile", "/admin/user-management/admins", "/admin/roles-permissions", "/admin/recycle-bin", "/admin/backup-restore", "/admin/system-settings"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("administration");
    }
  }, [location.pathname]);

  /* load notifications */
  useEffect(() => {
    axios.get(API_BASE_URL + "/api/notifications", { params: { role: "admin" } })
      .then(r => setNotifications(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNotifications([]));
  }, []);

  /* outside-click close */
  useEffect(() => {
    const h = (e) => {
      const clickedNotification =
        desktopNotifRef.current?.contains(e.target) ||
        mobileNotifRef.current?.contains(e.target);
      const clickedProfile =
        desktopProfileRef.current?.contains(e.target) ||
        mobileProfileRef.current?.contains(e.target);
      const clickedSidebar = sidebarRef.current?.contains(e.target);

      if (!clickedNotification) setNotifOpen(false);
      if (!clickedProfile) setProfileOpen(false);
      
      // Close sidebar on mobile when clicking outside
      if (window.innerWidth < 1024 && !clickedSidebar && !e.target.closest("button[data-sidebar-toggle]")) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("touchstart", h); };
  }, []);

  const unreadCount = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);

  const markRead = async (id) => {
    try {
      await axios.put(API_BASE_URL + `/api/notifications/${id}/read`);
      setNotifications(c => c.map(n => n._id === id ? { ...n, isRead: true } : n));
    } catch (e) { console.error(e); }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.delete(API_BASE_URL + "/api/notifications", { params: { role: "admin" } });
      setNotifications([]);
    } catch (e) { console.error(e); }
  };

  const handleLogout = () => {
    logout(navigate);
  };





  const resolveNotificationRoute = (notification) => {
    if (!notification) return "/dashboard";
    if (notification.type === "report-submitted" && notification.relatedId) {
      return `/reports/${notification.relatedId}`;
    }
    if (notification.type === "travel-created" || notification.type === "travel-status") {
      return "/admin/travel";
    }
    if (notification.relatedId) {
      return `/complaints?complaintId=${notification.relatedId}`;
    }
    return "/complaints";
  };

  const showCreateComplaintPaths = [
    "/dashboard", 
    "/",
    "/admin/vouchers",
    "/admin/gst-export-reports",
    "/admin/debit-credit-notes",
    "/admin/test-certificate-settings",
    "/admin/material-stock",
    "/admin/product-mapping"
  ];

  const showCreateComplaint = showCreateComplaintPaths.some(
    p => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  return (
    <div className="min-h-screen bg-background text-on-surface">
      {/* Overlay for Mobile/Collapsed Sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          id="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside 
        ref={sidebarRef}
        className={`fixed left-0 top-0 bottom-0 flex flex-col py-6 bg-inverse-surface dark:bg-inverse-surface w-[280px] z-50 overflow-y-auto transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`} 
        id="sidebar"
      >
        <div className="px-6 mb-8 flex justify-between items-center">
          <div>
            <h1 className="font-headline-lg text-headline-lg font-bold text-on-primary-container dark:text-primary-fixed">GENMECH</h1>
            <p className="font-label-caps text-label-caps text-primary-fixed/60 uppercase tracking-widest mt-1">Admin</p>
          </div>
          <button 
            className="text-on-primary-container/70 hover:text-on-primary-container lg:hidden" 
            onClick={() => setSidebarOpen(false)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <nav className="flex-1 space-y-1">
          {/* MOBILE NAVIGATION VIEW (lg:hidden) */}
          <div className="lg:hidden space-y-1">
            <NavLink 
              to="/dashboard"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/dashboard" ? "'FILL' 1" : "'FILL' 0" }}>dashboard</span>
              <span className="font-label-caps text-label-caps">Dashboard</span>
            </NavLink>

            <NavLink 
              to="/admin/dashboard-alerts"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/admin/dashboard-alerts" ? "'FILL' 1" : "'FILL' 0" }}>notifications_active</span>
              <span className="font-label-caps text-label-caps">Approvals &amp; Alerts</span>
            </NavLink>

            <NavLink 
              to="/admin/accounts-monitor"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/admin/accounts-monitor" ? "'FILL' 1" : "'FILL' 0" }}>monitoring</span>
              <span className="font-label-caps text-label-caps">Accounts Monitor</span>
            </NavLink>

            <NavLink 
              to="/operations"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/operations" ? "'FILL' 1" : "'FILL' 0" }}>engineering</span>
              <span className="font-label-caps text-label-caps">Operations</span>
            </NavLink>

            <NavLink 
              to="/sales-purchase"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/sales-purchase" ? "'FILL' 1" : "'FILL' 0" }}>payments</span>
              <span className="font-label-caps text-label-caps">Sales &amp; Purchase</span>
            </NavLink>

            <NavLink 
              to="/finance"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/finance" ? "'FILL' 1" : "'FILL' 0" }}>account_balance_wallet</span>
              <span className="font-label-caps text-label-caps">Accounts</span>
            </NavLink>

            <NavLink 
              to="/contacts"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/contacts" ? "'FILL' 1" : "'FILL' 0" }}>groups</span>
              <span className="font-label-caps text-label-caps">Contacts</span>
            </NavLink>

            <NavLink 
              to="/admin/administration"
              onClick={() => { setSidebarOpen(false); }}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/admin/administration" ? "'FILL' 1" : "'FILL' 0" }}>settings</span>
              <span className="font-label-caps text-label-caps">Administration</span>
            </NavLink>
          </div>

          {/* DESKTOP ACCORDION NAVIGATION VIEW (hidden lg:block) */}
          <div className="hidden lg:block space-y-1">
            <NavLink 
              to="/dashboard"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/dashboard" ? "'FILL' 1" : "'FILL' 0" }}>dashboard</span>
              <span className="font-label-caps text-label-caps">Dashboard</span>
            </NavLink>

            <NavLink 
              to="/admin/dashboard-alerts"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/admin/dashboard-alerts" ? "'FILL' 1" : "'FILL' 0" }}>notifications_active</span>
              <span className="font-label-caps text-label-caps">Approvals &amp; Alerts</span>
            </NavLink>

            <NavLink 
              to="/admin/accounts-monitor"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
                isActive 
                  ? "bg-primary-container text-on-primary-container border-primary-fixed"
                  : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/admin/accounts-monitor" ? "'FILL' 1" : "'FILL' 0" }}>monitoring</span>
              <span className="font-label-caps text-label-caps">Accounts Monitor</span>
            </NavLink>

            {/* Operations Accordion */}
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === "operations" ? null : "operations")}
                className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                  ["/create-complaint", "/complaints", "/admin/travel", "/admin/daily-updates"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                    ? "bg-primary-container/20 text-primary-fixed border-primary-fixed/30 font-bold"
                    : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/create-complaint", "/complaints", "/admin/travel", "/admin/daily-updates"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>engineering</span>
                  <span className="font-label-caps text-label-caps">Operations</span>
                </div>
                <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "operations" ? "rotate(180deg)" : "rotate(0deg)" }}>
                  expand_more
                </span>
              </button>
              {expandedSection === "operations" && (
                <div className="ml-8 pl-4 border-l border-outline-variant/30 flex flex-col gap-2 mt-2">
                  <NavLink 
                    to="/create-complaint"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Create Complaint
                  </NavLink>
                  <NavLink 
                    to="/complaints"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Complaints List
                  </NavLink>
                  <NavLink 
                    to="/admin/travel"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Travel Monitoring
                  </NavLink>
                  <NavLink 
                    to="/admin/daily-updates"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Daily Updates
                  </NavLink>
                </div>
              )}
            </div>

            {/* Sales & Purchase Accordion */}
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === "sales-purchase" ? null : "sales-purchase")}
                className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                  ["/admin/quotations", "/admin/performa-invoices", "/admin/sales-invoices", "/admin/purchase-orders", "/admin/purchase-invoices", "/admin/delivery-challans", "/admin/project-expenses"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                    ? "bg-primary-container/20 text-primary-fixed border-primary-fixed/30 font-bold"
                    : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/admin/quotations", "/admin/performa-invoices", "/admin/sales-invoices", "/admin/purchase-orders", "/admin/purchase-invoices", "/admin/delivery-challans", "/admin/project-expenses"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>payments</span>
                  <span className="font-label-caps text-label-caps">Sales &amp; Purchase</span>
                </div>
                <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "sales-purchase" ? "rotate(180deg)" : "rotate(0deg)" }}>
                  expand_more
                </span>
              </button>
              {expandedSection === "sales-purchase" && (
                <div className="ml-8 pl-4 border-l border-outline-variant/30 flex flex-col gap-2 mt-2">
                  <div className="space-y-3">
                    <div>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-3 mb-1">Sales</p>
                      <div className="flex flex-col gap-1 pl-2">
                        <NavLink 
                          to="/admin/quotations"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Quotation
                        </NavLink>
                        <NavLink 
                          to="/admin/performa-invoices"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Proforma Invoice
                        </NavLink>
                        <NavLink 
                          to="/admin/sales-invoices"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Sales Invoice
                        </NavLink>
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-3 mb-1">Purchases</p>
                      <div className="flex flex-col gap-1 pl-2">
                        <NavLink 
                          to="/admin/purchase-orders"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Purchase Order (PO)
                        </NavLink>
                        <NavLink 
                          to="/admin/purchase-invoices"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Purchase Invoice
                        </NavLink>
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-3 mb-1">Delivery Challans (DC)</p>
                      <div className="flex flex-col gap-1 pl-2">
                        <NavLink 
                          to="/admin/delivery-challans?type=returnable"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive && location.search.includes("returnable") ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Returnable DC
                        </NavLink>
                        <NavLink 
                          to="/admin/delivery-challans?type=non_returnable"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive && location.search.includes("non_returnable") ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Billing DC
                        </NavLink>
                        <NavLink 
                          to="/admin/delivery-challans?type=inward"
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                            isActive && location.search.includes("inward") ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                          }`}
                        >
                          Inward DC
                        </NavLink>
                      </div>
                    </div>

                    <NavLink 
                      to="/admin/project-expenses"
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                        isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5 text-slate-300"
                      }`}
                    >
                      Project Management
                    </NavLink>
                  </div>
                </div>
              )}
            </div>

            {/* Accounts Accordion */}
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === "accounts" ? null : "accounts")}
                className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                  ["/admin/vouchers", "/admin/gst-export-reports", "/admin/debit-credit-notes", "/admin/test-certificate-settings", "/admin/material-stock", "/admin/product-mapping", "/admin/accounts-monitor"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                    ? "bg-primary-container/20 text-primary-fixed border-primary-fixed/30 font-bold"
                    : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/admin/vouchers", "/admin/gst-export-reports", "/admin/debit-credit-notes", "/admin/test-certificate-settings", "/admin/material-stock", "/admin/product-mapping", "/admin/accounts-monitor"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>account_balance_wallet</span>
                  <span className="font-label-caps text-label-caps">Accounts</span>
                </div>
                <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "accounts" ? "rotate(180deg)" : "rotate(0deg)" }}>
                  expand_more
                </span>
              </button>
              {expandedSection === "accounts" && (
                <div className="ml-8 pl-4 border-l border-outline-variant/30 flex flex-col gap-2 mt-2">
                  <NavLink 
                    to="/admin/vouchers"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Finance / Voucher Management
                  </NavLink>
                  <NavLink 
                    to="/admin/gst-export-reports"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    GST Export &amp; Reports
                  </NavLink>
                  <NavLink 
                    to="/admin/debit-credit-notes"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Debit/Credit Notes
                  </NavLink>
                  <NavLink 
                    to="/admin/test-certificate-settings"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Test Certificate Settings
                  </NavLink>
                  <NavLink 
                    to="/admin/material-stock"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Material Stock
                  </NavLink>
                  <NavLink 
                    to="/admin/product-mapping"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Product Mapping
                  </NavLink>
                </div>
              )}
            </div>

            {/* Contacts Accordion */}
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === "contacts" ? null : "contacts")}
                className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                  ["/customers", "/customer-groups", "/admin/abb-warranty-complaints", "/abb-warranty-complaints", "/admin/dealers", "/admin/website-enquiries"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                    ? "bg-primary-container/20 text-primary-fixed border-primary-fixed/30 font-bold"
                    : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/customers", "/customer-groups", "/admin/abb-warranty-complaints", "/abb-warranty-complaints", "/admin/dealers", "/admin/website-enquiries"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>groups</span>
                  <span className="font-label-caps text-label-caps">Contacts</span>
                </div>
                <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "contacts" ? "rotate(180deg)" : "rotate(0deg)" }}>
                  expand_more
                </span>
              </button>
              {expandedSection === "contacts" && (
                <div className="ml-8 pl-4 border-l border-outline-variant/30 flex flex-col gap-2 mt-2">
                  <NavLink 
                    to="/customers"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Customers
                  </NavLink>
                  <NavLink 
                    to="/customer-groups"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Customer Groups
                  </NavLink>
                  <NavLink 
                    to="/admin/abb-warranty-complaints"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    ABB Warranty Complaints
                  </NavLink>
                  <NavLink 
                    to="/admin/dealers"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Suppliers
                  </NavLink>
                  <NavLink 
                    to="/admin/website-enquiries"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Website Enquiries
                  </NavLink>
                </div>
              )}
            </div>

            {/* Administration Accordion */}
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === "administration" ? null : "administration")}
                className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                  ["/admin/company-profile", "/engineers", "/admin/accounts", "/admin/user-management/admins", "/admin/roles-permissions", "/admin/recycle-bin", "/admin/backup-restore", "/admin/system-settings"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                    ? "bg-primary-container/20 text-primary-fixed border-primary-fixed/30 font-bold"
                    : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 border-transparent"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/admin/company-profile", "/engineers", "/admin/accounts", "/admin/user-management/admins", "/admin/roles-permissions", "/admin/recycle-bin", "/admin/backup-restore", "/admin/system-settings"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>settings</span>
                  <span className="font-label-caps text-label-caps">Administration</span>
                </div>
                <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "administration" ? "rotate(180deg)" : "rotate(0deg)" }}>
                  expand_more
                </span>
              </button>
              {expandedSection === "administration" && (
                <div className="ml-8 pl-4 border-l border-outline-variant/30 flex flex-col gap-2 mt-2">
                  <NavLink 
                    to="/admin/company-profile"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Company Profile
                  </NavLink>
                  <NavLink 
                    to="/engineers"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Engineers
                  </NavLink>
                  <NavLink 
                    to="/admin/accounts"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Accounts Personnel
                  </NavLink>
                  <NavLink 
                    to="/admin/user-management/admins"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Future Admin Users
                  </NavLink>
                  <NavLink 
                    to="/admin/roles-permissions"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Roles &amp; Permissions
                  </NavLink>
                  <NavLink 
                    to="/admin/recycle-bin"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Recycle Bin
                  </NavLink>
                  <NavLink 
                    to="/admin/backup-restore"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    Backup &amp; Restore
                  </NavLink>
                  <NavLink 
                    to="/admin/system-settings"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors block ${
                      isActive ? "text-primary-fixed font-bold bg-primary-fixed/10" : "text-primary-fixed/70 hover:text-primary-fixed hover:bg-primary-fixed/5"
                    }`}
                  >
                    System Settings
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        </nav>

        <div className="mt-auto px-2 space-y-1">
          <NavLink 
            to="/admin/profile"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `flex items-center gap-4 py-2 px-4 rounded-lg transition-colors ${isActive ? "bg-primary-container text-on-primary-container" : "text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10"}`}
          >
            <span className="material-symbols-outlined">account_circle</span>
            <span className="font-label-caps text-label-caps">My Profile</span>
          </NavLink>
          <button 
            onClick={handleLogout}
            className="w-full text-left text-on-primary-container/70 hover:text-on-primary-container hover:bg-surface-variant/10 flex items-center gap-4 py-2 px-4 rounded-lg transition-colors"
          >
            <span className="material-symbols-outlined">logout</span>
            <span className="font-label-caps text-label-caps">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Shell */}
      <main className={`min-h-screen transition-all duration-300 pb-8 ${
        sidebarOpen ? "lg:pl-[280px]" : "lg:pl-0"
      }`}>
        {/* Top Navigation Bar */}
        <header className="flex justify-between items-center w-full px-8 py-4 gap-md bg-transparent sticky top-0 z-30 backdrop-blur-md bg-background/80">
          <div className="flex items-center gap-4">
            <button 
              data-sidebar-toggle="true"
              className="p-2 hover:bg-surface-container-high rounded-full transition-colors active:opacity-80 flex items-center justify-center" 
              onClick={() => setSidebarOpen(prev => !prev)}
            >
              <span className="material-symbols-outlined text-primary">menu</span>
            </button>
            <span className="font-label-caps text-label-caps text-on-surface-variant/50">GENMECH OS</span>
          </div>

          <div className="flex items-center gap-6">
            {/* Notifications Dropdown */}
            <div ref={desktopNotifRef} className="relative">
              <button 
                onClick={() => setNotifOpen(c => !c)}
                className="relative cursor-pointer hover:bg-surface-container-high p-2 transition-colors rounded-full active:opacity-80 flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-primary">notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-error text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 z-30 w-80 max-h-[24rem] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-3 shadow-lg">
                  <div className="mb-2 flex items-center justify-between border-b pb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Notifications</p>
                    {notifications.length > 0 && (
                      <button onClick={clearAllNotifications} className="text-[11px] font-semibold text-red-500 hover:text-red-700">Clear all</button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-500">You're all caught up!</p>
                  ) : notifications.map(n => (
                    <button 
                      key={n._id} 
                      onClick={() => {
                        markRead(n._id);
                        setNotifOpen(false);
                        navigate(resolveNotificationRoute(n));
                      }} 
                      className={`mb-2 w-full rounded-xl px-3 py-3 text-left transition-colors border ${n.isRead ? "bg-gray-50 border-gray-100" : "bg-blue-50 border-blue-100"}`}
                    >
                      <p className="font-semibold text-gray-900 text-sm leading-snug">{n.title || n.message}</p>
                      {n.title && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.message}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div ref={desktopProfileRef} className="relative">
              <button 
                onClick={() => setProfileOpen(c => !c)}
                className="flex items-center gap-3 bg-surface-container-low px-4 py-2 rounded-full border border-outline-variant hover:bg-surface-container-high transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm">
                  {adminName.charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="font-label-caps text-[10px] text-on-surface-variant leading-none">LOGGED IN</span>
                  <span className="font-body-md text-body-md font-bold text-on-surface mt-0.5">{adminName}</span>
                </div>
              </button>
              {profileOpen && (
                <div className="absolute right-0 z-30 mt-2 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
                  <button 
                    onClick={() => { setProfileOpen(false); navigate("/admin/profile"); }} 
                    className="w-full rounded-xl px-4 py-3 text-left text-sm hover:bg-gray-50 font-semibold text-gray-700 transition-colors"
                  >
                    My Profile
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="w-full rounded-xl px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 font-semibold transition-colors"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content Canvas */}
        <div className="p-8 w-full max-w-none page-canvas">
          <Outlet />
        </div>
      </main>
      {showCreateComplaint && (
        <button 
          onClick={() => navigate("/create-complaint")}
          className="fixed bottom-8 right-8 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 active:scale-90 z-40"
        >
          <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>add</span>
        </button>
      )}
      <FloatingCalculator isShifted={showCreateComplaint} />
    </div>
  );
}
